import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { AlertCircle, ArrowRight, Baby, BarChart3, Brain, Calendar, CheckCircle, ChevronRight, ClipboardList, Clock, Eye, FileText, GraduationCap, Home, Phone, RefreshCw, Search, Shield, User, UserCheck, UserPlus, Users, X } from 'lucide-react';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalTrainingHandover = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [observers, setObservers] = useState([]);
  const [children, setChildren] = useState([]);
  const [trainingAssignments, setTrainingAssignments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [selectedTrainee, setSelectedTrainee] = useState(null);
  const [trainingDuration, setTrainingDuration] = useState(7); // days
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('principal_token');
      
      const [observersRes, childrenRes, trainingRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/principal/observers`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/principal/children`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/principal/training-assignments`, { params: { token } }).catch(() => ({ data: { assignments: [] } }))
      ]);
      
      setObservers(observersRes.data.observers || []);
      setChildren(childrenRes.data.children || []);
      setTrainingAssignments(trainingRes.data.assignments || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTraining = async () => {
    if (!selectedChild || !selectedMentor || !selectedTrainee) {
      alert('Please select a child, mentor, and trainee');
      return;
    }
    
    if (selectedMentor.id === selectedTrainee.id) {
      alert('Mentor and trainee must be different observers');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('principal_token');
      await axios.post(`${BACKEND_URL}/api/principal/training-assignments`, null, {
        params: {
          token,
          child_id: selectedChild.id,
          mentor_id: selectedMentor.id,
          trainee_id: selectedTrainee.id,
          duration_days: trainingDuration
        }
      });
      
      // Reset form
      setSelectedChild(null);
      setSelectedMentor(null);
      setSelectedTrainee(null);
      setShowAssignForm(false);
      
      // Reload data
      await loadData();
      alert('Training assignment created successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create training assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteTraining = async (assignmentId) => {
    if (!confirm('Complete this training and transfer the child fully to the trainee?')) return;
    
    try {
      const token = localStorage.getItem('principal_token');
      await axios.put(`${BACKEND_URL}/api/principal/training-assignments/${assignmentId}/complete`, null, {
        params: { token }
      });
      
      await loadData();
      alert('Training completed! Child has been transferred to the new observer.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to complete training');
    }
  };

  const handleCancelTraining = async (assignmentId) => {
    if (!confirm('Cancel this training assignment? Child will remain with original observer.')) return;
    
    try {
      const token = localStorage.getItem('principal_token');
      await axios.delete(`${BACKEND_URL}/api/principal/training-assignments/${assignmentId}`, {
        params: { token }
      });
      
      await loadData();
      alert('Training assignment cancelled.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to cancel training');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-slate-100 text-slate-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  // Filter children that have an observer assigned
  const assignedChildren = children.filter(c => c.observer_id);
  
  // Filter children for the form (those not in active training)
  const activeTrainingChildIds = trainingAssignments
    .filter(t => t.status === 'active')
    .map(t => t.child_id);
  
  const availableChildren = assignedChildren.filter(c => !activeTrainingChildIds.includes(c.id));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sanjaya-navy"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="training-handover-page">
      
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <GraduationCap className="w-7 h-7 text-indigo-600" />
                Observer Training & Handover
              </h1>
              <p className="text-slate-600">Train new observers by assigning shared students</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadData}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              <Button onClick={() => setShowAssignForm(true)} data-testid="new-training-btn">
                <UserPlus className="w-4 h-4 mr-2" /> New Training Assignment
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <GraduationCap className="w-10 h-10 opacity-80" />
                  <div>
                    <p className="text-3xl font-bold">
                      {trainingAssignments.filter(t => t.status === 'active').length}
                    </p>
                    <p className="text-sm opacity-80">Active Trainings</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-10 h-10 opacity-80" />
                  <div>
                    <p className="text-3xl font-bold">
                      {trainingAssignments.filter(t => t.status === 'completed').length}
                    </p>
                    <p className="text-sm opacity-80">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-500 to-violet-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Users className="w-10 h-10 opacity-80" />
                  <div>
                    <p className="text-3xl font-bold">{observers.length}</p>
                    <p className="text-sm opacity-80">Total Observers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Baby className="w-10 h-10 opacity-80" />
                  <div>
                    <p className="text-3xl font-bold">{availableChildren.length}</p>
                    <p className="text-sm opacity-80">Available for Training</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Active Training Assignments */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-sanjaya-navy" />
                Active Training Assignments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trainingAssignments.filter(t => t.status === 'active').length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <GraduationCap className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p>No active training assignments</p>
                  <p className="text-sm mt-2">Click "New Training Assignment" to start training an observer</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {trainingAssignments
                    .filter(t => t.status === 'active')
                    .map(assignment => (
                      <div 
                        key={assignment.id}
                        className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-6">
                            {/* Child */}
                            <div className="text-center">
                              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-1">
                                <Baby className="w-7 h-7 text-sanjaya-navy" />
                              </div>
                              <p className="font-semibold text-slate-800 text-sm">{assignment.child_name}</p>
                              <p className="text-xs text-slate-500">Child</p>
                            </div>
                            
                            {/* Arrow */}
                            <div className="flex flex-col items-center">
                              <div className="flex items-center gap-2 text-slate-400">
                                <div className="w-8 h-0.5 bg-gray-300"></div>
                                <ArrowRight className="w-5 h-5" />
                                <div className="w-8 h-0.5 bg-gray-300"></div>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">Shared</p>
                            </div>
                            
                            {/* Mentor */}
                            <div className="text-center">
                              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1">
                                <Shield className="w-7 h-7 text-green-600" />
                              </div>
                              <p className="font-semibold text-slate-800 text-sm">{assignment.mentor_name}</p>
                              <p className="text-xs text-green-600">Mentor</p>
                            </div>
                            
                            {/* Arrow */}
                            <div className="flex flex-col items-center">
                              <div className="flex items-center gap-2 text-slate-400">
                                <Eye className="w-5 h-5" />
                              </div>
                              <p className="text-xs text-slate-500 mt-1">Monitors</p>
                            </div>
                            
                            {/* Trainee */}
                            <div className="text-center">
                              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-1">
                                <GraduationCap className="w-7 h-7 text-amber-600" />
                              </div>
                              <p className="font-semibold text-slate-800 text-sm">{assignment.trainee_name}</p>
                              <p className="text-xs text-amber-600">Trainee</p>
                            </div>
                          </div>
                          
                          {/* Status & Actions */}
                          <div className="text-right">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(assignment.status)}`}>
                                {assignment.status}
                              </span>
                              <span className="text-sm text-slate-500">
                                {getDaysRemaining(assignment.end_date)} days left
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleCancelTraining(assignment.id)}
                              >
                                <X className="w-4 h-4 mr-1" /> Cancel
                              </Button>
                              <Button 
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleCompleteTraining(assignment.id)}
                                data-testid={`complete-training-${assignment.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" /> Complete & Transfer
                              </Button>
                            </div>
                            <p className="text-xs text-slate-400 mt-2">
                              Started: {new Date(assignment.start_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Completed Trainings */}
          {trainingAssignments.filter(t => t.status === 'completed').length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Completed Trainings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {trainingAssignments
                    .filter(t => t.status === 'completed')
                    .map(assignment => (
                      <div 
                        key={assignment.id}
                        className="p-3 bg-green-50 rounded-lg border border-green-200 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <CheckCircle className="w-6 h-6 text-green-600" />
                          <div>
                            <p className="font-medium text-slate-800">
                              {assignment.child_name} transferred to {assignment.trainee_name}
                            </p>
                            <p className="text-sm text-slate-500">
                              Trained by {assignment.mentor_name} • Completed {new Date(assignment.completed_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          Successful
                        </span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* New Training Assignment Modal */}
      {showAssignForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <GraduationCap className="w-6 h-6 text-indigo-600" />
                  New Training Assignment
                </h2>
                <button onClick={() => setShowAssignForm(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-slate-600 mt-1">Assign a child to both mentor and trainee for supervised training</p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Step 1: Select Child */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  1. Select Child to Share
                </label>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Search children..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto border rounded-lg">
                  {availableChildren
                    .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map(child => (
                      <div 
                        key={child.id}
                        className={`p-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 border-b last:border-b-0 ${
                          selectedChild?.id === child.id ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                        onClick={() => {
                          setSelectedChild(child);
                          // Auto-select current observer as mentor
                          const mentor = observers.find(o => o.id === child.observer_id);
                          if (mentor) setSelectedMentor(mentor);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <Baby className="w-5 h-5 text-blue-500" />
                          <div>
                            <p className="font-medium">{child.name}</p>
                            <p className="text-xs text-slate-500">
                              Current: {observers.find(o => o.id === child.observer_id)?.name || 'None'}
                            </p>
                          </div>
                        </div>
                        {selectedChild?.id === child.id && (
                          <CheckCircle className="w-5 h-5 text-sanjaya-navy" />
                        )}
                      </div>
                    ))}
                </div>
              </div>

              {/* Step 2: Mentor (auto-selected from current observer) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  2. Mentor (Current Observer)
                </label>
                <div className={`p-4 rounded-lg border-2 ${selectedMentor ? 'border-green-300 bg-green-50' : 'border-slate-200'}`}>
                  {selectedMentor ? (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Shield className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{selectedMentor.name}</p>
                        <p className="text-sm text-green-600">Will monitor trainee's calls</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center">Select a child first</p>
                  )}
                </div>
              </div>

              {/* Step 3: Select Trainee */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  3. Select Trainee (New Observer)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {observers
                    .filter(o => o.id !== selectedMentor?.id)
                    .map(observer => (
                      <div 
                        key={observer.id}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedTrainee?.id === observer.id 
                            ? 'border-amber-400 bg-amber-50' 
                            : 'border-slate-200 hover:border-amber-200'
                        }`}
                        onClick={() => setSelectedTrainee(observer)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            selectedTrainee?.id === observer.id ? 'bg-amber-100' : 'bg-slate-100'
                          }`}>
                            <GraduationCap className={`w-5 h-5 ${
                              selectedTrainee?.id === observer.id ? 'text-amber-600' : 'text-slate-500'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{observer.name}</p>
                            <p className="text-xs text-slate-500">{observer.assigned_children || 0} children</p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Step 4: Training Duration */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  4. Training Duration
                </label>
                <div className="flex items-center gap-4">
                  {[3, 7, 14, 30].map(days => (
                    <button
                      key={days}
                      className={`px-4 py-2 rounded-lg border-2 font-medium transition-all ${
                        trainingDuration === days
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 hover:border-indigo-200'
                      }`}
                      onClick={() => setTrainingDuration(days)}
                    >
                      {days} days
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary */}
              {selectedChild && selectedMentor && selectedTrainee && (
                <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                  <h4 className="font-semibold text-indigo-900 mb-2">Training Summary</h4>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Baby className="w-4 h-4 text-sanjaya-navy" />
                      <span>{selectedChild.name}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span>{selectedMentor.name}</span>
                    </div>
                    <Eye className="w-4 h-4 text-slate-400" />
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-amber-600" />
                      <span>{selectedTrainee.name}</span>
                    </div>
                  </div>
                  <p className="text-xs text-indigo-600 mt-2">
                    For {trainingDuration} days, {selectedMentor.name} will monitor {selectedTrainee.name}'s calls with {selectedChild.name}
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAssignForm(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateTraining}
                disabled={!selectedChild || !selectedMentor || !selectedTrainee || submitting}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="create-training-btn"
              >
                {submitting ? 'Creating...' : 'Create Training Assignment'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrincipalTrainingHandover;
